import React from 'react';
import {useTranslation} from 'react-i18next';
import ResultPanel from '../components/result-panel/result-panel';
import Text from '@/components/basic/text';
import theme from '@/style';
import {View, StyleSheet} from 'react-native';
import globalStore from '@/services/global.state';
import dayjs from 'dayjs';
import {sumMap, sumSuffixMap} from '@/common-pages/dice/constant';
import DiceItem from '@/common-pages/dice/component/dice-item';
import Ball from '@/common-pages/dice/component/ball';
import {goTo} from '@/utils';
import {getLastDiceOrder} from '../result-service';
import {DiceLastOrder} from '../result.type';

function isDiceLastOrder(order: unknown): order is DiceLastOrder {
  return Boolean(
    order &&
      typeof order === 'object' &&
      'issNo' in order &&
      'typeList' in order,
  );
}

function renderBySelectType(selectType: string) {
  if (!selectType) {
    return null;
  }
  if (sumMap[selectType]) {
    return <Ball ballSize={40} type={sumMap[selectType]} isNum={false} />;
  }
  const [diceType, diceNum] = selectType.split('_');
  if (diceType === 'sum') {
    return (
      <Ball
        ballSize={40}
        num={sumSuffixMap[diceNum] || 0}
        fontSize={22}
        isNum
      />
    );
  }
  if (diceType === 'single') {
    return <DiceItem diceSize={40} diceNum={sumSuffixMap[diceNum] || 0} />;
  }
  if (diceType === 'double') {
    return (
      <View style={[theme.flex.row, styles.smallDiceRow]}>
        <DiceItem diceSize={20} diceNum={sumSuffixMap[diceNum] || 0} />
        <DiceItem diceSize={20} diceNum={sumSuffixMap[diceNum] || 0} />
      </View>
    );
  }
  if (diceType === 'leopard') {
    return (
      <View style={styles.leopardWrap}>
        <View style={[theme.flex.center, styles.topDiceWrap]}>
          <DiceItem diceSize={20} diceNum={sumSuffixMap[diceNum] || 0} />
        </View>
        <View style={[theme.flex.row, styles.smallDiceRow]}>
          <DiceItem diceSize={20} diceNum={sumSuffixMap[diceNum] || 0} />
          <DiceItem diceSize={20} diceNum={sumSuffixMap[diceNum] || 0} />
        </View>
      </View>
    );
  }
  return null;
}

const DiceDraw = ({configId}: {configId: number}) => {
  const {t} = useTranslation();
  const [order, setOrder] = React.useState<DiceLastOrder>();

  const getResult = React.useCallback(async () => {
    try {
      const res = await getLastDiceOrder(configId);
      if (isDiceLastOrder(res)) {
        setOrder(res);
        return;
      }
      setOrder(undefined);
    } finally {
    }
  }, [configId]);

  React.useEffect(() => {
    if (configId && globalStore.token) {
      getResult().then();
    }
  }, [configId, getResult]);

  const bets = React.useMemo(() => {
    if (!order?.typeList?.length) {
      return null;
    }
    const targetList =
      order.totalAwardAmount > 0
        ? order.typeList.filter(item => item.awardAmount > 0)
        : order.typeList;
    return targetList.map((item, index) => (
      <View key={`${item.type}-${index}`}>{renderBySelectType(item.type)}</View>
    ));
  }, [order]);
  return order?.issNo ? (
    <>
      <ResultPanel
        containerStyle={styles.panelContainer}
        onMore={() => goTo('Bets', {tag: 'Dice'})}
        betInfo={
          <>
            <Text size="medium" fontFamily="fontInterBold" main>
              {order.issNo}
            </Text>
            <Text size="small" fontFamily="fontInterBold" main>
              {order.gameName}
            </Text>
          </>
        }
        winAmount={order.totalAwardAmount}
        betTime={dayjs(order.createTime).format('DD-MM-YYYY hh:mm A')}>
        <View style={styles.myBetsWrap}>
          <Text
            textAlign="center"
            accent
            size="small"
            style={styles.sectionTitle}>
            {t('game-page.label.myBets')}
          </Text>
          <View
            style={[
              theme.flex.center,
              theme.flex.row,
              theme.flex.wrap,
              styles.betWrap,
            ]}>
            {bets}
          </View>
        </View>
      </ResultPanel>
    </>
  ) : (
    <View />
  );
};

const styles = StyleSheet.create({
  panelContainer: {
    marginTop: 12,
    marginBottom: 4,
    marginHorizontal: 12,
  },
  myBetsWrap: {
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 8,
  },
  sectionTitle: {
    marginBottom: 2,
    opacity: 0.7,
  },
  betWrap: {
    paddingTop: 8,
    paddingBottom: 8,
    gap: theme.paddingSize.s,
  },
  smallDiceRow: {
    gap: theme.paddingSize.xxs,
  },
  topDiceWrap: {
    marginBottom: 2,
  },
  leopardWrap: {
    alignItems: 'center',
  },
});

export default DiceDraw;
